import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Building2, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import * as tenantsApi from '@/api/tenants.api';
import type { TenantCreado, TipoDeNegocio } from '@/api/tenants.api';
import { aSlug } from '@/lib/slug';

/**
 * MONTAR UN NEGOCIO NUEVO.
 *
 * Pedido textual de Esteban, en mayúsculas: *"COMO SEA MAS FACIL PARA MONTAR
 * MAS NUMEROS ALLI EN TENANT DIFERENTES"*. Mute Monkey, RESIST y Dreo son
 * negocios distintos, cada uno con su WhatsApp y su Inspector.
 *
 * El alta existía como servicio y como ruta desde antes; lo que faltaba era
 * esto: hasta hoy montar un negocio pedía una consola de base de datos.
 *
 * ═══ DOS COSAS QUE ESTA PANTALLA NO HACE ═══
 *
 * 1. **No conecta el WhatsApp.** Crea el canal en simulacro y para ahí. Vincular
 *    es escanear un QR, y un alta que además dispare una sesión de WhatsApp
 *    convierte un formulario en una acción con consecuencias afuera.
 *
 * 2. **No dice "listo" cuando falta algo.** El servidor devuelve `loQueFalta` y
 *    se muestra tal cual. Un alta que se declara completa y deja al dueño con un
 *    negocio a medias es peor que una que enumera lo que queda.
 */

const TIPOS: Array<{ id: TipoDeNegocio; nombre: string; para: string }> = [
  {
    id: 'personal',
    nombre: 'Personal (con Inspector)',
    para: 'Un WhatsApp propio. Lee las conversaciones sin marcarlas como vistas y te saca citas y compromisos.',
  },
  {
    id: 'atencion',
    nombre: 'Atención al cliente',
    para: 'Un agente de IA que contesta por ti, con pase a humano.',
  },
  {
    id: 'campanas',
    nombre: 'Campañas',
    para: 'Mandar mensajes a una lista, con el freno de bajas puesto.',
  },
  {
    id: 'relay',
    nombre: 'Cola de envío (sin IA)',
    para: 'Lee una cola externa, envía y reporta. No piensa: entrega lo que le dan.',
  },
];

export default function NuevoNegocio() {
  const toast = useToast();
  const [nombre, setNombre] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTocado, setSlugTocado] = useState(false);
  const [tipo, setTipo] = useState<TipoDeNegocio>('personal');
  const [email, setEmail] = useState('');
  const [nombreResp, setNombreResp] = useState('');
  const [password, setPassword] = useState('');
  const [crearCanal, setCrearCanal] = useState(true);
  const [hecho, setHecho] = useState<TenantCreado | null>(null);

  const crear = useMutation({
    mutationFn: () =>
      tenantsApi.crearTenant({
        nombre: nombre.trim(),
        slug: (slugTocado ? slug : aSlug(nombre)).trim(),
        tipo,
        responsable: {
          email: email.trim(),
          nombre: nombreResp.trim() || undefined,
          password: password || undefined,
          rol: 'ADMIN',
        },
        canal: crearCanal ? { nombre: `WhatsApp de ${nombre.trim()}` } : undefined,
      }),
    onSuccess: (r) => {
      setHecho(r);
      toast.success(`"${r.nombre}" quedó creado.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const slugEfectivo = slugTocado ? slug : aSlug(nombre);
  const puedeCrear = nombre.trim().length > 0 && slugEfectivo.length >= 2 && email.includes('@');

  if (hecho) {
    return (
      <div className="space-y-4">
        <PageHeader title="Negocio creado" description={`${hecho.nombre} · ${hecho.slug}`} />
        <Card>
          <CardBody className="space-y-3">
            <p className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-5 w-5" /> Quedó montado.
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {hecho.usuarioNuevo
                ? 'Se creó el usuario del responsable. Ya puede entrar con su correo.'
                : 'El responsable ya existía: se le dio acceso a este negocio.'}
            </p>

            {/* Lo que falta se dice. Un alta que se declara completa y deja un
                negocio a medias es peor que una que enumera lo que queda. */}
            {hecho.loQueFalta.length > 0 && (
              <div className="rounded-md bg-amber-50 p-3 text-sm dark:bg-amber-900/20">
                <p className="mb-1 font-medium text-amber-800 dark:text-amber-200">
                  Todavía falta:
                </p>
                <ul className="list-inside list-disc text-amber-700 dark:text-amber-300">
                  {hecho.loQueFalta.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-sm text-slate-600 dark:text-slate-300">
              {hecho.channelId
                ? 'El canal nació en simulacro: no manda nada real hasta que lo vincules y lo saques de simulacro desde Canales.'
                : 'No se creó ningún canal. Puedes agregarlo desde Canales.'}
            </p>

            <Button
              variant="secondary"
              onClick={() => {
                setHecho(null);
                setNombre('');
                setSlug('');
                setSlugTocado(false);
                setEmail('');
                setNombreResp('');
                setPassword('');
              }}
            >
              Montar otro
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Nuevo negocio"
        description="Cada negocio tiene su WhatsApp, su Inspector y su gente. No comparten nada."
      />

      <Card>
        <CardBody className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Cómo se llama</span>
            <input
              className="w-full max-w-md rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              value={nombre}
              placeholder="Mute Monkey"
              onChange={(e) => setNombre(e.target.value)}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Identificador corto</span>
            <input
              className="w-full max-w-xs rounded-md border border-slate-300 px-2 py-1 font-mono dark:border-slate-700 dark:bg-slate-900"
              value={slugEfectivo}
              onChange={(e) => {
                setSlugTocado(true);
                setSlug(aSlug(e.target.value));
              }}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Sale del nombre solo. Minúsculas, números y guiones.
            </span>
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-2">
          <p className="text-sm font-semibold">Qué hace este negocio</p>
          <p className="text-xs text-slate-500">
            Define con qué pantallas nace. Se puede cambiar después.
          </p>
          {TIPOS.map((t) => (
            <label key={t.id} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="tipo"
                className="mt-1"
                checked={tipo === t.id}
                onChange={() => setTipo(t.id)}
              />
              <span>
                <span className="font-medium">{t.nombre}</span>
                <span className="block text-xs text-slate-500">{t.para}</span>
              </span>
            </label>
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-3">
          <p className="text-sm font-semibold">Quién lo maneja</p>
          <p className="text-xs text-slate-500">
            Sin alguien que pueda entrar, el negocio queda inservible. Si el correo ya existe, se
            le da acceso a este negocio.
          </p>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Correo</span>
            <input
              type="email"
              className="w-full max-w-md rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              value={email}
              placeholder="alguien@ejemplo.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Nombre (opcional)</span>
            <input
              className="w-full max-w-md rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              value={nombreResp}
              onChange={(e) => setNombreResp(e.target.value)}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Clave inicial (si el usuario es nuevo)</span>
            <input
              type="password"
              className="w-full max-w-xs rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              value={password}
              placeholder="mínimo 8 caracteres"
              onChange={(e) => setPassword(e.target.value)}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Si lo dejas vacío y el usuario no existe, el alta te va a decir que falta.
            </span>
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={crearCanal}
              onChange={(e) => setCrearCanal(e.target.checked)}
            />
            <span className="font-medium">Crear su canal de WhatsApp</span>
          </label>
          <p className="text-xs text-slate-500">
            Nace en simulacro y sin acusar lectura. Vincular el número es escanear el QR desde
            Canales: esta pantalla no lo hace.
          </p>
        </CardBody>
      </Card>

      <Button onClick={() => crear.mutate()} disabled={!puedeCrear || crear.isPending}>
        <Building2 className="h-4 w-4" /> {crear.isPending ? 'Montando…' : 'Montar el negocio'}
      </Button>
    </div>
  );
}
